import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Breadcrumb from "../ProfileCommon/Breadcrumb";
import NewHeader from "../../Home/NewHeader";
import QuestHistoryCard from "../ProfileCommon/QuestHistoryCard";
import { useLazyGetQuestHistoryListQuery } from "../../../services/apiSlice";
import { convertUTCToLocalString } from "../../../Utils/helper";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";

const QuestHistory = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [getListQuestsHistory, { isFetching }] =
    useLazyGetQuestHistoryListQuery();

  const [questList, setQuestList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const observerRef = useRef(null);
  const lastFetchPageRef = useRef(0); // Track the last fetched page
  const limit = 5; // Fixed limit per page

  const fetchQuests = useCallback(
    async (currentPage) => {
      if (
        isFetching ||
        !hasMore ||
        isLoading ||
        currentPage <= lastFetchPageRef.current
      ) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await getListQuestsHistory({
          page: currentPage,
          limit,
        }).unwrap();
        const newQuests = response?.data || [];

        // Update quest list with new unique quests only
        setQuestList((prev) => {
          const existingIds = new Set(prev.map((q) => q.id));
          const uniqueNew = newQuests.filter((q) => !existingIds.has(q.id));
          return [...prev, ...uniqueNew];
        });

        // If fewer quests than limit, no more to load
        setHasMore(newQuests.length === limit);
        lastFetchPageRef.current = currentPage;
      } catch (err) {
        console.error("Failed to fetch quests:", err);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    },
    [getListQuestsHistory, isFetching, hasMore, isLoading, limit]
  );

  // Initial fetch
  useEffect(() => {
    if (page === 1 && !isFetching && !isLoading) {
      fetchQuests(1);
    }
  }, [fetchQuests, isFetching, page, isLoading]);

  // Fetch more quests when page changes
  useEffect(() => {
    if (page > 1) {
      fetchQuests(page);
    }
  }, [page, fetchQuests]);

  // Infinite scroll via observer with debounce
  const lastElementRef = useCallback(
    (node) => {
      if (!node || isFetching || !hasMore || isLoading) {
        return;
      }

      if (observerRef.current) observerRef.current.disconnect();

      let debounceTimeout;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !isFetching &&
            !isLoading &&
            hasMore
          ) {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(() => {
              setPage((prev) => prev + 1);
            }, 300); // Debounce for 300ms
          }
        },
        { threshold: 0.1 }
      );

      observerRef.current.observe(node);
    },
    [isFetching, hasMore, isLoading]
  );

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  return (
    <div className="flex overflow-hidden flex-col mx-auto w-full bg-white max-w-[480px]">
      <LoadingCommon statusLoading={isFetching || isLoading} />
      <div className="flex flex-col w-full min-h-[912px]">
        <div className=" fixed top-0 left-0 w-full z-50">
          <NewHeader />
        </div>
        <div className="h-[30px]"></div>
        <div className="flex flex-col flex-1 justify-start gap-3 py-3.5 w-full px-[30px] mt-4">
          <Breadcrumb
            breadCrumbTiltle={t("profile.questHistory")}
            onClickBack={() => navigate("/profile")}
          />

          {questList.length > 0 ? (
            questList.map((quest, index) => (
              <div
                key={quest.id || index}
                ref={
                  index === questList.length - 1 ? lastElementRef : undefined
                }
                className="mb-4"
              >
                <QuestHistoryCard
                  title={
                    quest?.quest?.quest_business?.business?.company_name ?? ""
                  }
                  question={quest?.quest?.quest_business?.name ?? ""}
                  status={quest?.status ?? ""}
                  timestamp={convertUTCToLocalString(quest?.start_time)}
                />
              </div>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">
              {t("profile.noQuests")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestHistory;
